import {type RefObject, useEffect, useLayoutEffect, useState} from 'react'

export type ScrollbarSizes = {
  /** Thickness of the horizontal scrollbar, which eats block size. */
  horizontal: number
  /** Thickness of the vertical scrollbar, which eats inline size. */
  vertical: number
}

const NONE: ScrollbarSizes = {horizontal: 0, vertical: 0}

/**
 * How thick a scroll container's scrollbars are, in pixels.
 *
 * A scroll container's border box includes its scrollbars and its client box does not,
 * so the difference is the thickness, plus any border. Both are equally "not scrollable
 * content", which is what the board wants the number for. It is zero on a platform that
 * overlays scrollbars, and it changes when a scrollbar appears, when the container
 * resizes, and when someone changes the setting for it, so it is measured rather than
 * assumed.
 *
 * The board needs the number twice: its auto-scroll buffers are measured from the
 * container's rectangle, which includes the scrollbars, and its scroll padding has to
 * keep a focused card from landing underneath one.
 */
export function useMeasureScrollbars(scrollRef: RefObject<HTMLElement | null>): ScrollbarSizes {
  const [sizes, setSizes] = useState<ScrollbarSizes>(NONE)

  // Read once before paint, so the first frame is not laid out against zeroes.
  useLayoutEffect(() => {
    const element = scrollRef.current
    if (!element) return

    setSizes(measure(element))
  }, [scrollRef])

  // Everything after that is observed, off the render path.
  useEffect(() => {
    const element = scrollRef.current
    if (!element) return

    const observer = new ResizeObserver(() => {
      const current = scrollRef.current
      if (!current) return

      const next = measure(current)
      setSizes(previous =>
        previous.horizontal === next.horizontal && previous.vertical === next.vertical ? previous : next,
      )
    })

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [scrollRef])

  return sizes
}

function measure(element: HTMLElement): ScrollbarSizes {
  return {
    horizontal: element.offsetHeight - element.clientHeight,
    vertical: element.offsetWidth - element.clientWidth,
  }
}
